import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import multipleADUsLotImage from '@/assets/multiple-adus-lot.jpg';

const HowManyADUsCanYouHaveOnOneLotCalifornia = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <>
      <CanonicalTag url="https://www.udaadu.com/multiple-adus-one-lot-california" />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative bg-white rounded-lg w-full max-w-4xl h-full max-h-[90vh]">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              ✕
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 py-8">
          {/* Hero Section */}
          <div className="mb-12">
            <img 
              src={multipleADUsLotImage} 
              alt="Multiple ADUs on single lot aerial view" 
              className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
            />
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
              How Many ADUs Can You Have on One Lot in California?
            </h1>
            
            <p className="text-lg md:text-xl text-white leading-relaxed mb-8">
              Find out how many ADUs you can legally build on a single lot in California, including regulations, zoning differences, and multi-family exceptions.
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                California State Rules for Multiple ADUs
              </h2>
              <p className="text-white leading-relaxed mb-6">
                California law generally permits one ADU and one JADU per single-family residential lot, allowing a total of two accessory dwelling units. The state has expanded ADU allowances in recent years while maintaining local jurisdiction authority over specific implementation details. Multi-family properties may qualify for additional ADUs based on existing unit counts and zoning classifications.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Single-family vs. Multi-family Lot ADU Allowances
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Single-family lots typically accommodate one ADU plus one JADU under current regulations. Multi-family properties may build additional ADUs equal to 25% of existing units, creating opportunities for multiple accessory dwellings on larger residential developments. Lot size, zoning district, and existing infrastructure capacity influence actual ADU limits.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                City-specific Regulations Explained
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Local jurisdictions can impose additional restrictions on ADU numbers through zoning codes, design standards, and infrastructure requirements. Some cities encourage multiple ADUs through incentive programs, while others limit numbers based on neighborhood character or utility capacity. Parking requirements, setback rules, and lot coverage limits affect practical ADU development potential.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Frequently Asked Questions
              </h2>
              <div className="text-white leading-relaxed space-y-6">
                <div>
                  <p className="font-semibold mb-2">Q: How many ADUs are typically allowed on residential lots?</p>
                  <p>A: Usually one ADU and one JADU on single-family lots; multi-family lots may allow more.</p>
                </div>
                
                <div>
                  <p className="font-semibold mb-2">Q: Can cities restrict how many ADUs I build?</p>
                  <p>A: Yes, local zoning rules impact the number and size of allowable ADUs per lot.</p>
                </div>
              </div>
            </section>
          </div>

          {/* Call-to-Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Button 
              onClick={openCalendlyModal}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 text-lg"
            >
              Book & Schedule
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8 py-3 text-lg"
            >
              <Link to="/landing">Learn More</Link>
            </Button>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default HowManyADUsCanYouHaveOnOneLotCalifornia;